import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiPhone, FiMail, FiMapPin } from "react-icons/fi";
import { FaMountain, FaCommentDots } from "react-icons/fa";
import SEO from "../common/SEO";
import HeaderOne from "../common/header/HeaderOne";
import FooterTwo from "../common/footer/FooterTwo";
import "../common/styles/panel.css";
import { useTranslation } from "react-i18next";

const AboutUs = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 576px)');
    const handleChange = (e) => setIsMobile(e.matches);
    handleChange(mq);
    mq.addEventListener ? mq.addEventListener('change', handleChange) : mq.addListener(handleChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handleChange) : mq.removeListener(handleChange);
    };
  }, []);

  // 团队成员数据（用于渲染团队板块）
  const teamMembers = [
    {
      img: 'William.jpg',
      alt: 'William Jiang',
      nameKey: 'about_us.team_members.william.name',
      positionKey: 'about_us.team_members.william.position'
    },
    {
      img: 'Jax.jpg',
      alt: 'Jax Jin',
      nameKey: 'about_us.team_members.jax.name',
      positionKey: 'about_us.team_members.jax.position'
    },
    {
      img: 'Gary.jpg',
      alt: 'Gary Gao',
      nameKey: 'about_us.team_members.gary.name',
      positionKey: 'about_us.team_members.gary.position'
    },
    {
      img: 'Lin.jpg',
      alt: 'Lin Yuan',
      nameKey: 'about_us.team_members.lin.name',
      positionKey: 'about_us.team_members.lin.position'
    }
  ];

  // 联系方式数据（用于渲染Contact板块）
  const contactItems = [
    {
      icon: <FiPhone color="#fff" size={40} />,
      textKey: 'about_us.contact.phone',
      buttonKey: 'about_us.contact.call_button'
    },
    {
      icon: <FiMail color="#fff" size={40} />,
      textKey: 'about_us.contact.email',
      buttonKey: 'about_us.contact.email_button'
    },
    {
      icon: <FiMapPin color="#fff" size={40} />,
      textKey: 'about_us.contact.location',
      buttonKey: 'about_us.contact.consult_button'
    }
  ];

  // Google Maps嵌入URL - 使用useMemo确保语言变化时重新计算
  const mapUrl = useMemo(() => {
    // 语言映射
    const lang = {
      'en': 'en',
      'zh': 'zh-CN'
    };
    return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3271.1289475543736!2d138.6214102772162!3d-34.92830287471521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ab0ceb35eb6c4b9%3A0x4bf54554557ae46e!2zbDIvMTM1IEZ1bGxhcnRvbiBSZCwgUm9zZSBQYXJrIFNBIDUwNjfmvrPlpKfliKnkupo!5e0!3m2!1s${lang[i18n.language] || 'en'}!2shk!4v1755846053994!5m2!1s${lang[i18n.language] || 'en'}!2shk`;
  }, [i18n.language]);

  const handleStartNavigation = () => {
    // 使用更明确的地址格式以确保谷歌地图能够准确定位
    const address = "L2/135 Fullarton Rd, Rose Park SA 5067, Australia";
    const encodedAddress = encodeURIComponent(address);
    // 打开谷歌地图导航
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    // 如果上面的方法仍然不起作用，可以尝试直接使用坐标
    // window.open(`https://www.google.com/maps/dir/?api=1&destination=-34.9258,138.6007`, '_blank');
  };

  // 交互：拨打电话 / 发送邮件 / 跳转咨询
  const handleCallUs = () => {
    window.location.href = "tel:+61871100999";
  };

  const handleEmailUs = () => {
    window.location.href = "mailto:info@cyberate.investments";
  };

  const handleFreeConsult = () => {
    history.push("/FreeConsult");
  };

  return (
    <>
      <SEO title="About Us - Cyberate Investments" />
      <main className="page-wrapper">
        <HeaderOne btnStyle="round" HeaderSTyle="header-not-transparent" />

        {/* Hero Section */}
        <div className="hero-section" style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner/aboutus.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: isMobile ? '320px' : '500px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)'
          }}></div>
          <div className="container position-relative" style={{ zIndex: 2 }}>
            <div className="row">
              <div className="col-lg-12 text-center">
                <h1 style={{
                  color: '#fff',
                  fontSize: isMobile ? '36px' : '60px',
                  fontWeight: '700',
                  marginBottom: isMobile ? '12px' : '20px',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}>
                  {t("about_us.title")}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Mission & Vision Section */}
        <div className="container page-content-text" style={{ paddingTop: isMobile ? 40 : 80, paddingBottom: isMobile ? 40 : 80 }}>
          <div className="cy-panel cy-panel--p40 cy-panel-aboutus" style={{ marginBottom: 60 }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '40px' }}>
              <div style={{ flex: 1, paddingRight: isMobile ? 0 : '40px' }}>
                <div className="mission-section">
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'transparent',
                      borderRadius: '50%',
                      marginRight: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FaMountain color="#fff" size={28} />
                    </div>
                    <h3 style={{ color: '#fff', fontWeight: '700', margin: 0 }}>{t("about_us.mission.title")}</h3>
                  </div>
                  <p style={{ color: '#ccc', fontSize: isMobile ? '16px' : '20px', lineHeight: '1.6' }}>
                    {t("about_us.mission.content")}
                  </p>
                </div>
              </div>
              <div style={{ flex: 1, paddingLeft: isMobile ? 0 : '40px' }}>
                <div className="vision-section">
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'transparent',
                      borderRadius: '50%',
                      marginRight: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FaCommentDots color="#fff" size={28} />
                    </div>
                    <h3 style={{ color: '#fff', fontWeight: '700', margin: 0 }}>{t("about_us.vision.title")}</h3>
                  </div>
                  <p style={{ color: '#ccc', fontSize: isMobile ? '16px' : '20px', lineHeight: '1.6' }}>
                    {t("about_us.vision.content")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Brand Story Section */}
          <div className="cy-panel cy-panel--p40 cy-panel-aboutus" style={{ marginBottom: 60 }}>
            <h2 style={{ color: '#fff', fontWeight: '700', textAlign: 'center', marginBottom: isMobile ? '24px' : '40px', fontSize: isMobile ? '36px' : undefined }}>
              {t("about_us.brand_story.title")}
            </h2>
            <div style={{ color: '#ccc', fontSize: isMobile ? '16px' : '20px', lineHeight: '1.8' }}>
              <p style={{ marginBottom: '16px' }}>
                {t("about_us.brand_story.content")}
              </p>
            </div>
          </div>

          {/* Team Members Section */}
          <div className="cy-panel cy-panel--p40 cy-panel-aboutus" style={{ marginBottom: 60 }}>
            <h2 style={{ color: '#fff', fontWeight: '700', textAlign: 'center', marginBottom: isMobile ? '24px' : '40px', fontSize: isMobile ? '36px' : undefined }}>
              {t("about_us.team_members.title")}
            </h2>
            <div className="row">
              {teamMembers.map((m, idx) => (
                <div className="col-lg-3 col-md-6 col-sm-6 col-6" style={{ marginBottom: 30 }} key={idx}>
                  <div className="team-member" style={{ textAlign: 'center' }}>
                    <div className="member-image" style={{
                      width: isMobile ? 'auto' : '216px',
                      height: isMobile ? '240px' : '288px',
                      background: '#d9d9d9',
                      borderRadius: '12px',
                      margin: '0 auto 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666'
                    }}>
                      <img src={`${process.env.PUBLIC_URL}/images/team2/${m.img}`} alt={m.alt} style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }} />
                    </div>
                    <div style={{ maxWidth: isMobile ? '180px' : '216px', margin: '0 auto' }}>
                      <h4 style={{ color: 'rgba(192, 143, 85, 1)', fontSize: isMobile ? '18px' : '20px', fontWeight: '600', marginBottom: '10px' }}>
                        {t(m.nameKey)}
                      </h4>
                      <p style={{ color: '#ccc', fontSize: isMobile ? '14px' : '16px', textAlign: 'left' }}>
                        {t(m.positionKey)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="cy-panel cy-panel--p40 cy-panel-aboutus" style={{ marginBottom: 60 }}>
            <h2 style={{ color: '#fff', fontWeight: '700', textAlign: 'center', marginBottom: isMobile ? '16px' : '20px', maxWidth: '800px', margin: '0 auto', fontSize: isMobile ? '36px' : undefined }}>
              {t("about_us.contact.title")}
            </h2>
            <p style={{ color: 'rgba(192, 143, 85, 1)', fontSize: isMobile ? '16px' : '18px', textAlign: 'center', marginBottom: isMobile ? '24px' : '40px' }}>
              {t("about_us.contact.subtitle")}
            </p>
            <div className="row">
              {contactItems.map((c, idx) => (
                <div className="col-lg-4 col-md-6 col-sm-12" style={{ marginBottom: 30 }} key={idx}>
                  <div className="contact-card" style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '30px',
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    <div style={{
                      width: isMobile ? '48px' : '60px',
                      height: isMobile ? '48px' : '60px',
                      background: 'transparent',
                      borderRadius: '50%',
                      margin: '0 auto 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {c.icon}
                    </div>
                    <h4 style={{ color: '#fff', fontSize: isMobile ? '16px' : '18px', marginBottom: '15px' }}>
                      {t(c.textKey)}
                    </h4>
                    <button onClick={() => (idx === 0 ? handleCallUs() : idx === 1 ? handleEmailUs() : handleFreeConsult())} style={{
                      background: 'linear-gradient(90deg, #FFAE2C 0%, #FFD945 100%)',
                      color: '#000',
                      border: 'none',
                      borderRadius: '25px',
                      padding: isMobile ? '10px 20px' : '12px 24px',
                      fontWeight: '600',
                      fontSize: isMobile ? '14px' : undefined,
                      cursor: 'pointer'
                    }}>
                      {t(c.buttonKey)}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Office Location Section */}
          <div className="cy-panel cy-panel--p40 cy-panel-aboutus">
            <h2 style={{ color: '#fff', fontWeight: '700', textAlign: 'center', marginBottom: isMobile ? '16px' : '20px', fontSize: isMobile ? '36px' : undefined }}>
              {t("about_us.office_location.title")}
            </h2>
            <p style={{ color: '#ccc', fontSize: isMobile ? '16px' : '18px', textAlign: 'center', marginBottom: isMobile ? '24px' : '40px' }}>
              {t("about_us.office_location.subtitle")}
            </p>
            <div className="map-container" style={{
              height: isMobile ? '300px' : '400px',
              borderRadius: '12px',
              marginBottom: '30px',
              overflow: 'hidden'
            }}>
              <iframe
                src={mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Maps - Cyberate Investments Office Location"
              ></iframe>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleStartNavigation}
                style={{
                  background: 'linear-gradient(90deg, #FFAE2C 0%, #FFD945 100%)',
                  color: '#000',
                  border: 'none',
                  borderRadius: '25px',
                  padding: '15px 30px',
                  fontWeight: '600',
                  fontSize: '18px',
                  cursor: 'pointer'
                }}
              >
                {t("about_us.office_location.navigation_button")}
              </button>
            </div>
          </div>
        </div>

        <FooterTwo />
      </main>
    </>
  );
};

export default AboutUs;
